import { ChangeEvent, useEffect, useRef, useState } from "react";
import {
  CButton, CFormGroup, CLabel, CModal, CModalBody, CModalFooter,
  CModalHeader, CModalTitle, CSelect, CTextarea,
} from "@coreui/react";
import { createItem } from "../../api/generics";
import { downloadSifenReport, SifenReportDraft, SifenReportOptions } from "../../api/sifen-reports";
import { SUCCESS } from "../../utils/constants/tags";

const PORTAL_URL = "https://servicios.set.gov.py/eset-publico/EnvioMailSetIService.do";
const PORTAL_ATTACHMENT_LIMIT = 2 * 1024 * 1024;

interface Props {
  show: boolean;
  documentType: string;
  documentId?: number;
  onClose: () => void;
}

const SifenReportModal = ({ show, documentType, documentId, onClose }: Props) => {
  const [draft, setDraft] = useState<SifenReportDraft>();
  const [options, setOptions] = useState<SifenReportOptions>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [feedback, setFeedback] = useState("");
  const [current, setCurrent] = useState(false);
  const textField = useRef<HTMLTextAreaElement>(null);
  const baseUrl = `/sifen_reports/${documentType}/${documentId}`;
  const reportText = draft ? `${draft.subject}\n\n${draft.text}` : "";

  useEffect(() => {
    if (!show || documentId === undefined) return;
    let active = true;
    setDraft(undefined); setOptions({}); setError(""); setFeedback(""); setCurrent(false); setBusy(true);
    createItem<SifenReportDraft, SifenReportOptions>(`${baseUrl}/preview/`, {})
      .then(result => {
        if (!active) return;
        if (result.status !== SUCCESS || !result.data) {
          setError(result.detail || "No se pudo preparar el reporte.");
          return;
        }
        setDraft(result.data);
        setOptions({ issue: result.data.issue, templateId: result.data.templateId, details: "" });
        setCurrent(true);
      })
      .catch(() => { if (active) setError("No se pudo preparar el reporte."); })
      .finally(() => { if (active) setBusy(false); });
    return () => { active = false; };
  }, [show, documentId, baseUrl]);

  const change = (values: Partial<SifenReportOptions>) => {
    setOptions(previous => ({ ...previous, ...values }));
    setCurrent(false); setError(""); setFeedback("");
  };

  const generate = async () => {
    if (busy || documentId === undefined) return;
    setBusy(true); setError(""); setFeedback("");
    try {
      const result = await createItem<SifenReportDraft, SifenReportOptions>(`${baseUrl}/preview/`, options);
      if (result.status !== SUCCESS || !result.data) {
        setError(result.detail || "No se pudo preparar el reporte.");
        return;
      }
      setDraft(result.data);
      setOptions(previous => ({ ...previous, issue: result.data!.issue, templateId: result.data!.templateId }));
      setCurrent(true);
    } catch { setError("No se pudo preparar el reporte. Intente nuevamente."); }
    finally { setBusy(false); }
  };

  const copy = async () => {
    setFeedback("");
    try {
      if (!navigator.clipboard?.writeText) throw new Error("Clipboard unavailable");
      await navigator.clipboard.writeText(reportText);
      setFeedback("Texto copiado. Péguelo en Consulta o mensaje del formulario de la DNIT.");
    } catch {
      textField.current?.focus();
      textField.current?.select();
      setFeedback("Seleccione y copie el texto con Ctrl+C o ⌘C.");
    }
  };

  const download = async () => {
    if (busy || !current) return;
    setBusy(true); setError(""); setFeedback("");
    try {
      const blob = await downloadSifenReport(baseUrl, options);
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = draft?.archiveFilename || `reporte-sifen-${documentId}.zip`;
      document.body.appendChild(link);
      link.click(); link.remove();
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
      if (blob.size > PORTAL_ATTACHMENT_LIMIT)
        setError("El ZIP descargado supera los 2 MB admitidos por el formulario de la DNIT. Revise el tamaño de los archivos antes de adjuntarlo.");
      else setFeedback("ZIP descargado con el texto del reporte y todos los XML de respaldo.");
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : "No se pudo descargar el ZIP de respaldo.");
    } finally { setBusy(false); }
  };

  return <CModal show={show} size="xl" onClose={() => { if (!busy) onClose(); }} closeOnBackdrop={!busy}>
    <CModalHeader closeButton={!busy}><CModalTitle>Preparar reporte para DNIT</CModalTitle></CModalHeader>
    <CModalBody className="text-left">
      <ol>
        <li>Abra el formulario de la DNIT y complete Nombre y Apellido y su Correo electrónico de contacto.</li>
        <li>Seleccione el tipo de consulta correspondiente al problema con MARANGATU o facturación electrónica.</li>
        <li>Copie el texto generado y péguelo en Consulta o mensaje.</li>
        <li>Descargue el ZIP y adjúntelo como Documento de respaldo (máximo 2 MB). Revise y envíe el formulario.</li>
      </ol>
      <p className="text-muted">La presentación se realiza en el formulario de la DNIT. Preparar o descargar este reporte no crea una incidencia en SmartDoc.</p>
      <p><a href={PORTAL_URL} target="_blank" rel="noopener noreferrer">Abrir formulario de la DNIT</a></p>
      {error && <div className="alert alert-danger" role="alert">{error}</div>}
      {feedback && <p role="status">{feedback}</p>}
      {busy && <p role="status">Preparando reporte...</p>}
      {draft && <>
        <fieldset disabled={busy}>
          <CFormGroup>
            <CLabel htmlFor="sifen-report-issue">Tipo de problema</CLabel>
            <CSelect id="sifen-report-issue" value={options.issue} onChange={(event: ChangeEvent<HTMLSelectElement>) => change({ issue: event.target.value, templateId: undefined })}>
              {draft.issues.map(issue => <option key={issue.id} value={issue.id}>{issue.label}</option>)}
            </CSelect>
          </CFormGroup>
          <CFormGroup>
            <CLabel htmlFor="sifen-report-details">Observaciones adicionales</CLabel>
            <CTextarea id="sifen-report-details" rows={3} maxLength={4000} value={options.details || ""} onChange={(event: ChangeEvent<HTMLTextAreaElement>) => change({ details: event.target.value })} placeholder="Puede indicar el período consultado y la fecha de la última sincronización visible en Marangatu." />
          </CFormGroup>
        </fieldset>
        {!current && <p className="alert alert-info">Actualice el texto para incluir sus cambios antes de copiarlo o descargar el ZIP.</p>}
        {current && <>
          <CLabel htmlFor="sifen-report-text">Texto para Consulta o mensaje</CLabel>
          <textarea ref={textField} id="sifen-report-text" className="form-control" rows={14} readOnly value={reportText} />
          <div className="my-3"><CButton color="primary" disabled={busy} onClick={copy}>Copiar texto</CButton></div>
          <p className="mt-3 mb-1"><strong>Archivos incluidos en el ZIP:</strong> reporte.txt y los siguientes XML:</p>
          <ul>{draft.attachments.map(filename => <li key={filename} style={{ overflowWrap: "anywhere" }}>{filename}</li>)}</ul>
        </>}
        <CButton color="primary" disabled={busy || !current} onClick={download}>Descargar ZIP</CButton>
      </>}
    </CModalBody>
    <CModalFooter>
      <CButton color="secondary" disabled={busy} onClick={onClose}>Cerrar</CButton>
      <CButton color="info" disabled={busy || documentId === undefined} onClick={generate}>Actualizar</CButton>
    </CModalFooter>
  </CModal>;
};

export default SifenReportModal;
